import { ChangeDetectorRef, Directive, ElementRef, EventEmitter, Input, NgZone, OnInit, Optional, Output, Self } from '@angular/core';
import { NgControl } from '@angular/forms';
import {
  AllowCharacterValidationResult,
  CharacterSpec, mapToValidationError, removeValidationError,
  testCharSpecWithFallback,
  validateAllowedCharacters
} from './allow-characters.model';


interface DisallowCharactersEvent {
  char: string;
  reason: AllowCharacterValidationResult | undefined;
}

@Directive({
  selector: '[appAllowCharacters]'
})
export class AllowCharactersDirective implements OnInit {

  @Input() allowCharacters: CharacterSpec | undefined = undefined;
  @Input() disallowCharacters: CharacterSpec | undefined = undefined;

  private disabled = false;

  @Input()
  set appAllowCharacters(enabled: boolean) {
    this.disabled = !enabled;
    this.addListener(this.disabled);

  }

  @Output() disallowedCharactersDetected = new EventEmitter<DisallowCharactersEvent>();

  constructor(private elementRef: ElementRef,
              @Optional() @Self() private ngControl: NgControl) {

  }

  ngOnInit(): void {
    this.addListener(this.disabled);
  }

  onPaste = (event: ClipboardEvent) => {
    const pastedText = event.clipboardData?.getData('text/plain') || '';
    // Timeout set as angular is not setting error otherwise to component.
    // Related ticket: https://github.com/angular/angular/issues/38191
    // TODO: Remove when angular team releases a fix for bug
    setTimeout(() => {
      this.executeValidation(pastedText);
    }, 0);

  }

  onKeyPress = (event: KeyboardEvent): void => {
    if (this.disabled) {
      return;
    }
    const char = this.getKeyForEvent(event);
    this.executeValidation(char, event);
  }

  onBlur = (): void => {
    if (this.disabled) {
      return;
    }

    if (this.ngControl) {
      this.executeValidation(this.ngControl.value);
      return;
    }
    if (this.elementRef) {
      this.executeValidation(this.elementRef.nativeElement.value);
      return;
    }
  }

  private executeValidation(char: string, event?: Event): void {
    // Check if the entered character matches the provided regular expression
    const validation =
      validateAllowedCharacters(char, this.allowCharacters, this.disallowCharacters);

    if (validation) {
      // If not allowed characters are detected we won't allow to enter
      event?.preventDefault();
    }
    this.updateControlError(this.ngControl, validation);
    this.disallowedCharactersDetected.emit({
      char,
      reason: validation || undefined,
    });
  }

  private addListener(disabled: boolean): void {
    if (disabled) {
      this.removeHostListener();
    } else {
      this.addHostListener();
    }
  }

  private addHostListener(): void {
    this.elementRef.nativeElement.addEventListener('keypress', this.onKeyPress);
    this.elementRef.nativeElement.addEventListener('blur', this.onBlur);
    this.elementRef.nativeElement.addEventListener('paste', this.onPaste);
  }

  private removeHostListener(): void {
    this.elementRef.nativeElement.removeEventListener('keypress', this.onKeyPress);
    this.elementRef.nativeElement.removeEventListener('blur', this.onBlur);
    this.elementRef.nativeElement.removeEventListener('paste', this.onPaste);
  }

  private getKeyForEvent(event: KeyboardEvent): string {
    if (event.key) {
      return event.key;
    } else {
      const charCode = event.which || event.keyCode;
      return String.fromCharCode(charCode);
    }
  }

  private updateControlError(ngControl: NgControl, validation?: AllowCharacterValidationResult): void {
    const control = ngControl.control;
    if (control) {
      const controlErrors = control.errors || {};

      const errors = validation && { ...controlErrors, ...mapToValidationError(validation)}
        || removeValidationError(controlErrors);
      const hasErrors = Object.keys(errors).length > 0;
      if (hasErrors || !validation) {
        control.markAsTouched();
        control.markAllAsTouched();
      }
      control.setErrors(hasErrors ? errors : null, {
        emitEvent: true
      });

    }
  }


}
